import type {
  BottomSheetBackdropProps,
  BottomSheetFooterProps,
  BottomSheetScrollViewMethods,
} from '@gorhom/bottom-sheet';
import {
  BottomSheetBackdrop,
  BottomSheetFooter,
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetView,
  INITIAL_LAYOUT_VALUE,
  useBottomSheetInternal,
} from '@gorhom/bottom-sheet';
import type { ReactNode } from 'react';
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollViewProps,
  ViewProps,
} from 'react-native';
import { Keyboard, View, useWindowDimensions } from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  ReduceMotion,
  useDerivedValue,
} from 'react-native-reanimated';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import {
  UnistylesRuntime,
  useUnistyles,
  withUnistyles,
} from 'react-native-unistyles';

import { Box } from '@squire/ui';

import type { ScrollMetrics } from '@/hooks/shared/useScrollOverflowObserver';
import { useScrollOverflowObserver } from '@/hooks/shared/useScrollOverflowObserver';
import { useWebDialogBehavior } from '@/hooks/shared/useWebDialogBehavior';
import { useWidget } from '@/providers/WidgetProvider';
import { getContentColumnInset } from '@/utils/layoutUtils';

import { sheetContainerA11yProps } from './containerA11yProps';
import { styles } from './styles';
import type { BottomSheetProps, BottomSheetSnapPoint } from './types';

export interface BottomSheetRef {
  open: (animated?: boolean) => void;
  close: (animated?: boolean) => void;
  snapTo: (index: number, animated?: boolean) => void;
  snapToIndex: (index: number, animated?: boolean) => void;
  /**
   * Scrolls the sheet's scrollable content to a given offset. No-op when the
   * sheet was rendered with `scrollable={false}` (it renders a plain
   * `BottomSheetView`, which has no scroll position to move).
   *
   * Optional so every pre-existing `BottomSheetRef`-shaped object literal
   * (test mocks, other imperative handles across the app) keeps compiling
   * unchanged — callers that care should optional-chain (`?.scrollTo?.(...)`)
   * the same way they already do for the rest of this interface.
   */
  scrollTo?: (options: { y: number; animated?: boolean }) => void;
}

const StyledBottomSheet = withUnistyles(BottomSheetModal);
const ThemedStop = withUnistyles(Stop);

type SheetFooterSlot = {
  footer: ReactNode;
  style?: ViewProps['style'];
  showFade?: boolean;
};

// Sub-pixel leftovers from fractional offsets must still count as the end.
const SCROLL_END_TOLERANCE = 1;
const FADE_DURATION_MS = 150;
const SHADOW_OPACITY = '0.12';

const SheetFooterFade = () => {
  const gradientId = useId();
  return (
    <Animated.View
      testID="bottom-sheet-footer-fade"
      entering={FadeIn.duration(FADE_DURATION_MS).reduceMotion(
        ReduceMotion.System,
      )}
      exiting={FadeOut.duration(FADE_DURATION_MS).reduceMotion(
        ReduceMotion.System,
      )}
      style={styles.footerFade}
    >
      <Svg width="100%" height="100%">
        <Defs>
          <LinearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <ThemedStop
              offset="0"
              stopOpacity="0"
              uniProps={(t) => ({ stopColor: t.backgroundInverseStatic })}
            />
            <ThemedStop
              offset="1"
              stopOpacity={SHADOW_OPACITY}
              uniProps={(t) => ({ stopColor: t.backgroundInverseStatic })}
            />
          </LinearGradient>
        </Defs>
        <Rect width="100%" height="100%" fill={`url(#${gradientId})`} />
      </Svg>
    </Animated.View>
  );
};

const SheetFooter = ({
  footer,
  style,
  showFade,
  ...props
}: BottomSheetFooterProps & SheetFooterSlot) => (
  <BottomSheetFooter {...props}>
    <View testID="bottom-sheet-footer" style={[styles.footer, style]}>
      {showFade ? <SheetFooterFade /> : null}
      {footer}
    </View>
  </BottomSheetFooter>
);

// gorhom's own BottomSheetFooterContainer lifts the footer above the software
// keyboard; this one keeps it at the sheet's bottom edge so the keyboard
// covers it instead of pushing it up over the content.
const SheetFooterContainer = (slot: SheetFooterSlot) => {
  const { animatedLayoutState, animatedPosition } = useBottomSheetInternal();
  const animatedFooterPosition = useDerivedValue(() => {
    const { handleHeight, footerHeight, containerHeight } =
      animatedLayoutState.get();
    if (handleHeight === INITIAL_LAYOUT_VALUE) {
      return 0;
    }
    const sheetHeight = Math.max(0, containerHeight - animatedPosition.get());
    return sheetHeight - footerHeight - handleHeight;
  }, [animatedLayoutState, animatedPosition]);

  return (
    <SheetFooter {...slot} animatedFooterPosition={animatedFooterPosition} />
  );
};

const useHasContentBelowFold = () => {
  const metrics = useRef<ScrollMetrics>({
    offsetY: 0,
    contentHeight: 0,
    layoutHeight: 0,
  });
  const [hasContentBelow, setHasContentBelow] = useState(false);

  const update = (patch: Partial<ScrollMetrics>) => {
    Object.assign(metrics.current, patch);
    const { offsetY, contentHeight, layoutHeight } = metrics.current;
    setHasContentBelow(
      layoutHeight > 0 &&
        contentHeight - layoutHeight - offsetY > SCROLL_END_TOLERANCE,
    );
  };

  return { hasContentBelow, update };
};

const BottomSheet = forwardRef<BottomSheetRef, BottomSheetProps>(
  (
    {
      name,
      style,
      onOpen = () => {},
      hideBackdrop = false,
      defaultSnapPoint,
      children,
      onSnapChange,
      onDismiss,
      dismissible = true,
      closeOnDragDown = true,
      enablePanGesture = true,
      testID = 'bottom-sheet',
      containerStyle,
      sheetContainerStyle,
      scrollable = true,
      hideHandle = false,
      maxDynamicContentRatio = 0.9,
      scrollProps,
      viewProps,
      accessibilityLabel,
      closeOnEscape,
      footer,
      footerStyle,
      ...props
    },
    ref,
  ) => {
    // withUnistyles infers the modal's data type as unknown, so match it here.
    const internalRef = useRef<BottomSheetModal<unknown>>(null);
    const dialogRef = useRef<View>(null);
    const { isWidget } = useWidget();
    const [isPresented, setIsPresented] = useState(false);
    const scrollViewRef = useRef<BottomSheetScrollViewMethods>(null);

    // Insets the whole sheet to the content column on wide viewports, so the
    // panel stops bleeding edge to edge on desktop while its content sits
    // centred inside it. Applied to the sheet's own `style` rather than
    // `containerStyle`, which wraps the backdrop too — the backdrop should keep
    // dimming the whole screen.
    //
    // Plain numbers, not `styles.maxWidthWrapper`: @gorhom renders these nodes
    // itself, and a non-Unistyles component never resolves a breakpoint object,
    // so a maxWidth set that way silently does nothing. Derived from
    // useWindowDimensions so it follows a resize.
    const { width: windowWidth } = useWindowDimensions();
    const panelInset = getContentColumnInset(windowWidth);

    // `backgroundStyle` is forwarded straight through to @gorhom's
    // BottomSheetModal — unlike `style`/`contentContainerStyle`,
    // `withUnistyles` doesn't reactively patch it, so `styles.background`
    // alone froze at whatever theme was active on mount. Reading
    // `backgroundL0` here subscribes this component to theme changes so the
    // sheet's own background repaints with the sheet's active theme instead
    // of leaking the app's pinned-light page background (SQR-14766 web
    // dark-mode white seam).
    const { theme: unistylesTheme } = useUnistyles();

    const enableDynamicSizing =
      props.enableDynamicSizing ?? !props.snapPoints?.length;

    // Sampled once: on iOS Safari the software keyboard shrinks
    // window.innerHeight, which UnistylesRuntime.screen.height mirrors on web.
    // Re-reading it on a keyboard-time re-render collapsed the sheet to the
    // transient height and it never grew back (SQR-15160).
    // The ratio is also capped at the top safe-area inset: gorhom does not
    // clamp the dynamic detent itself, and a high ratio on Dynamic Island
    // iPhones otherwise renders the sheet behind the island.
    const [maxDynamicContentSize] = useState(() => {
      if (props.maxDynamicContentSize !== undefined) {
        return props.maxDynamicContentSize;
      }
      const { screen, insets } = UnistylesRuntime;
      return Math.min(
        screen.height * maxDynamicContentRatio,
        screen.height - insets.top,
      );
    });

    const snapPoints = props.snapPoints?.map(
      (point: BottomSheetSnapPoint): string | number =>
        point.percentage ?? point.height,
    );

    const StyledBottomSheetContent = useMemo(
      () => withUnistyles(scrollable ? BottomSheetScrollView : BottomSheetView),
      [scrollable],
    );

    const hasFooter = footer !== undefined && footer !== null;
    const { hasContentBelow, update: updateScrollMetrics } =
      useHasContentBelowFold();
    const trackScrollEdge = hasFooter && scrollable;
    // The scroll view mounts on present, so the observer arms on isPresented.
    useScrollOverflowObserver(
      scrollViewRef,
      trackScrollEdge && isPresented,
      updateScrollMetrics,
    );
    // Same inset as `backgroundStyle`, so the footer spans the content column.
    const footerSlot = useMemo<SheetFooterSlot>(
      () => ({
        footer,
        style: [{ marginHorizontal: panelInset }, footerStyle],
        showFade: trackScrollEdge && hasContentBelow,
      }),
      [footer, footerStyle, panelInset, trackScrollEdge, hasContentBelow],
    );

    // The fade needs the offset, content and viewport heights; gorhom only
    // hands `onScroll` over through runOnJS, so these stay JS-side state.
    const scrollEdgeProps: ScrollViewProps | undefined = trackScrollEdge
      ? {
          scrollEventThrottle: scrollProps?.scrollEventThrottle ?? 16,
          onLayout: (event: LayoutChangeEvent) => {
            scrollProps?.onLayout?.(event);
            updateScrollMetrics({
              layoutHeight: event.nativeEvent.layout.height,
            });
          },
          onContentSizeChange: (width: number, height: number) => {
            scrollProps?.onContentSizeChange?.(width, height);
            updateScrollMetrics({ contentHeight: height });
          },
          onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
            scrollProps?.onScroll?.(event);
            const { contentOffset, contentSize, layoutMeasurement } =
              event.nativeEvent;
            updateScrollMetrics({
              offsetY: contentOffset.y,
              contentHeight: contentSize.height,
              layoutHeight: layoutMeasurement.height,
            });
          },
        }
      : undefined;

    const handleBottomSheetChange = (index: number) => {
      if (index === 0) onOpen?.();
      if (index >= 0) {
        setIsPresented(true);
        onSnapChange?.(index);
      } else {
        setIsPresented(false);
      }
    };

    const handleDismiss = () => {
      setIsPresented(false);
      onDismiss?.();
      Keyboard.dismiss();
    };

    // Inside the widget the sheet is a modal dialog for keyboard and screen
    // reader users; outside it keeps today's behaviour (SQR-13995, D12-3).
    useWebDialogBehavior(dialogRef, {
      active: isWidget && isPresented,
      onDismiss: () => internalRef.current?.dismiss(),
      dismissable: closeOnEscape ?? dismissible,
      initialFocus: 'container',
    });

    const renderHandle = () => {
      if (hideHandle) return null;
      return (
        <Box
          style={[
            styles.handleContainer,
            // @ts-expect-error - TODO: fix this
            styles.maxWidthWrapper,
            styles.topRounded,
            props.handleStyle,
          ]}
        >
          <Box style={[styles.handleIndicator, props.handleIndicatorStyle]} />
        </Box>
      );
    };

    const BackdropComponent = (backdropProps: BottomSheetBackdropProps) => {
      if (hideBackdrop) return null;
      return (
        <BottomSheetBackdrop
          {...backdropProps}
          opacity={1}
          appearsOnIndex={0}
          disappearsOnIndex={-1}
          pressBehavior={dismissible ? 'close' : 'none'}
          style={styles.backdrop}
        />
      );
    };

    useImperativeHandle(ref, () => ({
      open: () => internalRef.current?.present(),
      close: () => internalRef.current?.forceClose(),
      snapTo: (index: number) => internalRef.current?.snapToIndex(index),
      snapToIndex: (index: number) => internalRef.current?.snapToIndex(index),
      scrollTo: (options: { y: number; animated?: boolean }) => {
        if (!scrollable) return;
        // Deferred to the next frame: calling this synchronously from an
        // onLayout callback can race the browser's own layout settle for
        // the content that just mounted, silently cancelling an in-flight
        // smooth-scroll (`Element.scroll({ behavior: 'smooth' })`) on web.
        requestAnimationFrame(() => {
          scrollViewRef.current?.scrollTo?.({
            y: options.y,
            animated: options.animated,
          });
        });
      },
    }));

    const content = (
      <StyledBottomSheetContent
        // @ts-expect-error - StyledBottomSheetContent's ref type is a union
        // of BottomSheetScrollView's and BottomSheetView's ref types
        // depending on `scrollable`; scrollViewRef only cares about the
        // scrollable case, which is the one that ever exposes scrollTo.
        ref={scrollViewRef}
        testID={testID}
        enableFooterMarginAdjustment={hasFooter}
        {...(scrollable ? scrollProps : viewProps)}
        {...scrollEdgeProps}
        style={[
          styles.content,
          hideHandle && styles.topRounded,
          !scrollable && [styles.maxWidthWrapper, containerStyle],
          style,
        ]}
        contentContainerStyle={[
          hideHandle && styles.topRounded,
          // @ts-expect-error - withUnistyles resolves the breakpoints in
          // styles.maxWidthWrapper at runtime; reanimated types the prop.
          scrollable && [styles.maxWidthWrapper, containerStyle],
        ]}
      >
        {children}
      </StyledBottomSheetContent>
    );

    // A child, not gorhom's `footerComponent`, so inside the widget it stays
    // within the `aria-modal` dialog wrapper and its Tab trap.
    const pinnedFooter = hasFooter ? (
      <SheetFooterContainer {...footerSlot} />
    ) : null;

    return (
      <>
        <StyledBottomSheet
          {...props}
          {...sheetContainerA11yProps}
          name={name}
          ref={internalRef}
          index={defaultSnapPoint}
          stackBehavior="push"
          snapPoints={snapPoints}
          onDismiss={handleDismiss}
          enableHandlePanningGesture={enablePanGesture}
          maxDynamicContentSize={maxDynamicContentSize}
          enableContentPanningGesture={enablePanGesture}
          keyboardBehavior="interactive"
          keyboardBlurBehavior="restore"
          onChange={handleBottomSheetChange}
          enableDynamicSizing={enableDynamicSizing}
          handleComponent={renderHandle}
          containerStyle={sheetContainerStyle}
          backgroundStyle={[
            styles.background,
            // @ts-expect-error - BottomSheetProps types `backgroundStyle` as
            // ViewStyle minus the position props, because @gorhom owns the
            // panel's vertical placement. Horizontal insets are the one part it
            // doesn't drive: it stretches the panel edge to edge and offers no
            // width prop. Insetting here is the only lever that reaches the
            // painted panel — the sheet's own `style` insets the container but
            // leaves the background full-bleed behind it.
            { left: panelInset, right: panelInset },
            { backgroundColor: unistylesTheme.backgroundL0 },
            props.backgroundStyle,
          ]}
          enableOverDrag={false}
          backdropComponent={BackdropComponent}
          enablePanDownToClose={
            enablePanGesture && dismissible && closeOnDragDown
          }
        >
          {isWidget ? (
            <View
              ref={dialogRef}
              role="dialog"
              aria-modal
              aria-label={accessibilityLabel}
              tabIndex={-1}
              testID={`${testID}-dialog`}
              style={styles.dialog}
            >
              {content}
              {pinnedFooter}
            </View>
          ) : (
            <>
              {content}
              {pinnedFooter}
            </>
          )}
        </StyledBottomSheet>
      </>
    );
  },
);

BottomSheet.displayName = 'BottomSheet';

export { BottomSheet };
